import { useState, useEffect, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { Toast } from 'primereact/toast';
import { Card } from 'primereact/card';
import { Dialog } from 'primereact/dialog';
import { SortOrder } from 'primereact/api';
import findSaldoClienteCompraService from '../../service/findSaldoClienteCompra';
import putSaldoClienteCompraPagamentoService from '../../service/putSaldoClienteCompraPagamento/putSaldoClienteCompraPagamentoService';
import { useGlobalState } from '../../components/globalState/GlobalStateProvider';
import QrCodePix from './QrCodePix';

interface SaldoClienteListProps {
  onEdit?: (saldoCliente: any) => void;
  refresh?: boolean;
  cliente:string;
  onRefresh?: () => void; // Callback para atualizar saldo e movimento
}

export default function SaldoClienteList({ onEdit, refresh, cliente, onRefresh }: SaldoClienteListProps) {
  const toast = useRef<Toast>(null);
  const [saldoClienteCompras, setSaldoClienteCompras] = useState(undefined);
  const [totalRecords, setTotalRecords] = useState(0);
  const {state} = useGlobalState();
  const [showQrCodeModal, setShowQrCodeModal] = useState(false);
  const [selectedQrCode, setSelectedQrCode] = useState<string | null>(null);
  const [selectedCompraValor, setSelectedCompraValor] = useState<number>(0);
  const [lazyState, setlazyState] = useState({
    first: 0,
    rows: 10,
    page: 1,
    cliente: cliente,
    sortField: 'data',
    sortOrder: SortOrder.DESC
  });

  const loadLazyData = () => {
    findSaldoClienteCompraService(lazyState).then(data => {setTotalRecords(data.total);setSaldoClienteCompras(data.compras)}).catch(() => {setTotalRecords(0);setSaldoClienteCompras(undefined)});
  }

  const onPage = (event: any) => {
    setlazyState({
      first: event.first,
      rows: event.rows,
      page: (event.first / event.rows) + 1,
      cliente: cliente,
      sortField: event.sortField,
      sortOrder: event.sortOrder
    });
  };
  const onSort = (event:any) => {
    setlazyState({
      first: 0,
      rows: 10,
      page: 1,
      cliente: cliente,
      sortField: event.sortField,
      sortOrder: event.sortOrder
    });
  };
  
  useEffect(() => {
    loadLazyData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[lazyState, refresh])

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatDate = (value: Date) => {
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(new Date(value));
  };

  const getTipoCompraLabel = (tipo: string) => {
    const tipos: { [key: string]: string } = {
      'CREDITO_PREPAGO': 'Crédito Pré-pago',
      'PAGAMENTO_VISTA': 'Pagamento à Vista',
      'FATURAMENTO_MENSAL': 'Faturamento Mensal'
    };
    return tipos[tipo] || tipo;
  };

  const getStatusSeverity = (status: string) => {
    switch (status) {
      case 'APROVADO': return 'success';
      case 'PENDENTE': return 'warning';
      case 'REJEITADO': return 'danger';
      default: return 'info';
    }
  };

  const getStatusLabel = (status: string) => {
    const labels: { [key: string]: string } = {
      'APROVADO': 'Aprovado',
      'PENDENTE': 'Pendente',
      'REJEITADO': 'Rejeitado'
    };
    return labels[status] || status;
  };

  const handleRegistrarPagamento = async (saldoCliente: any) => {
    try {
      await putSaldoClienteCompraPagamentoService(saldoCliente);
      
      toast.current?.show({
        severity: 'success',
        summary: 'Sucesso',
        detail: 'Pagamento registrado com sucesso!',
        life: 3000
      });
      
      // Recarregar a lista atual
      loadLazyData();
      
      // Callback para atualizar saldo e movimento na tela pai
      if (onRefresh) {
        onRefresh();
      }
    } catch (error: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Erro',
        detail: error.message || 'Erro ao registrar pagamento',
        life: 5000
      });
    }
  };

  // Template columns
  const valorTemplate = (rowData: any) => {
    return formatCurrency(rowData.valor);
  };

  const dataTemplate = (rowData: any) => {
    return formatDate(rowData.data);
  };

  const dataBaixaTemplate = (rowData: any) => {
    if(rowData.dataPagamento){
      return formatDate(rowData.dataPagamento);
    }
    return '';
  };

  const tipoCompraTemplate = (rowData: any) => {
    return getTipoCompraLabel(rowData.tipoCompra);
  };

  const statusTemplate = (rowData: any) => {
    return (
      <Tag 
        value={getStatusLabel(rowData.status)} 
        severity={getStatusSeverity(rowData.status) as any}
      />
    );
  };

  const handleViewQrCode = (rowData: any) => {
    setSelectedQrCode(rowData.chavePix);
    setSelectedCompraValor(rowData.valor);
    setShowQrCodeModal(true);
  };

  const handleCloseQrCodeModal = () => {
    setShowQrCodeModal(false);
    setSelectedQrCode(null);
    setSelectedCompraValor(0);
  };

  const actionTemplate = (rowData: any) => {
    const isPendente = rowData.status === 'PENDENTE';
    const hasChavePix = rowData.chavePix;
    
    return (
      <div className="flex-consulta gap-2">
        {isPendente && hasChavePix && (
          <Button
            icon="pi pi-qrcode"
            size="large"
            rounded 
            text 
            style={{color: 'rgb(24, 52, 98)'}}
            onClick={() => handleViewQrCode(rowData)}
            tooltip="Visualizar QR Code"
          />
        )}
        {isPendente && state.user.funcoes.includes("SALDO_CLIENTE_BAIXA_MANUAL") && (
          <Button
            icon="pi pi-shopping-cart"
            rounded 
            text 
            size="large"
            style={{color: 'rgb(24, 52, 98)'}}
            onClick={() => handleRegistrarPagamento(rowData)}
            tooltip="Registrar pagamento"
          />
        )}
      </div>
    );
  };

  const header = (
    <div className="flex flex-wrap gap-2 align-items-center justify-content-between">
      <h4 className="m-0">Histórico de Compras de Saldo</h4>
    </div>
  );

  return (
    <div>
      <Toast ref={toast} />
      
      <Card className="shadow-2">
        <DataTable value={saldoClienteCompras} first={lazyState.first} dataKey="id" paginator={true}  lazy={true}
                                emptyMessage="Sem registros" header={header}
                                onSort={onSort} sortField={lazyState.sortField} sortOrder={lazyState.sortOrder}
                                rows={lazyState.rows} totalRecords={totalRecords} onPage={onPage}
                                tableStyle={{ minWidth: '75rem' }}>
        <Column 
          field="data" 
          header="Data" 
          body={dataTemplate}
          sortable 
          style={{ minWidth: '8rem' }}
        ></Column>
        <Column 
          field="usuario" 
          header="Comprado por" 
          style={{ minWidth: '8rem' }}
        ></Column>
        <Column 
          field="valor" 
          header="Valor" 
          body={valorTemplate}
          sortable 
          style={{ minWidth: '8rem' }}
        ></Column>
        <Column 
          field="tipoCompra" 
          header="Tipo" 
          body={tipoCompraTemplate}
          sortable 
          style={{ minWidth: '10rem' }}
        ></Column>
        <Column 
          field="dataPagamento" 
          header="Data da baixa" 
          body={dataBaixaTemplate}
          sortable 
          style={{ minWidth: '8rem' }}
        ></Column>
        <Column 
          field="baixadoPor" 
          header="Baixado por" 
          style={{ minWidth: '8rem' }}
        ></Column>
        <Column 
          field="status" 
          header="Status" 
          body={statusTemplate}
          sortable 
          style={{ minWidth: '8rem' }}
        ></Column>
        <Column 
          body={actionTemplate} 
          exportable={false} 
          style={{ minWidth: '8rem' }}
        ></Column>
      </DataTable>
      </Card>

      {/* Modal do QR Code */}
      <Dialog
        visible={showQrCodeModal}
        onHide={handleCloseQrCodeModal}
        header="QR Code PIX"
        style={{ width: '450px' }}
        modal
        dismissableMask
      >
        {selectedQrCode && (
          <QrCodePix 
            chavePix={selectedQrCode} 
            valor={selectedCompraValor}
            showValor={true}
          />
        )}
      </Dialog>
    </div>
  );
}